import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, ComposedChart, Area
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Calendar,
  PoundSterling,
  Gavel,
  Building2,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Users,
  Clock,
  Home,
  FileText,
  ArrowUpRight,
  Factory,
  Target,
  Repeat,
  Info
} from "lucide-react";
import { getLegalPerformanceAnalysis, getLegalPerformanceAnalysisEnriched, getCreditPolicySummary, getCreditPolicyLoanBook, getCreditPolicyIndustryComparison, getCreditPolicyHomeownerAnalysis, enrichSicCodes, fuzzySearchCompany, matchCompanyToCase } from "@/lib/api";
import { toast } from "sonner";

// Color palette
const COLORS = {
  primary: "#3B70E0",
  danger: "#ef4444",
  warning: "#f59e0b",
  success: "#22c55e",
  purple: "#a855f7",
  teal: "#14b8a6",
  pink: "#ec4899",
  indigo: "#6366f1",
};

const CHART_COLORS = ["#3B70E0", "#ef4444", "#22c55e", "#f59e0b", "#a855f7", "#14b8a6", "#ec4899", "#6366f1", "#64748b"];

export default function LegalPerformanceAnalysis() {
  const [loading, setLoading] = useState(true);
  const [enrichedLoading, setEnrichedLoading] = useState(false);
  const [basicData, setBasicData] = useState(null);
  const [enrichedData, setEnrichedData] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Credit Policy Intelligence state
  const [policyLoading, setPolicyLoading] = useState(false);
  const [policyData, setPolicyData] = useState(null);
  const [loanBookData, setLoanBookData] = useState(null);
  const [industryComparison, setIndustryComparison] = useState(null);
  const [homeownerAnalysis, setHomeownerAnalysis] = useState(null);
  const [sicEnrichmentLoading, setSicEnrichmentLoading] = useState(false);
  // Emma #6 — handling / jurisdiction filters (matched via portal case markers)
  const [handlingFilter, setHandlingFilter] = useState("all");
  const [jurisdictionFilter, setJurisdictionFilter] = useState("all");

  const loadBasicData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (handlingFilter !== "all") params.handling = handlingFilter;
      if (jurisdictionFilter !== "all") params.jurisdiction = jurisdictionFilter;
      const data = await getLegalPerformanceAnalysis(params);
      setBasicData(data);
    } catch (error) {
      console.error("Failed to load legal performance data:", error);
      toast.error("Failed to load legal performance data");
    } finally {
      setLoading(false);
    }
  }, [handlingFilter, jurisdictionFilter]);

  const loadEnrichedData = useCallback(async () => {
    try {
      setEnrichedLoading(true);
      toast.info("Fetching enriched data from LoanDisk & Companies House...");
      const data = await getLegalPerformanceAnalysisEnriched(true, true, 8);
      console.log("Enriched data received:", data);
      if (data && data.success) {
        setEnrichedData(data);
        toast.success(`Enriched ${data.total_enriched} cases with external data`);
      } else {
        toast.error("No enriched data returned");
      }
    } catch (error) {
      console.error("Failed to load enriched data:", error);
      toast.error(`Failed to load enriched data: ${error.message}`);
    } finally {
      setEnrichedLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBasicData();
  }, [loadBasicData]);

  // Load Credit Policy data
  const loadCreditPolicyData = useCallback(async () => {
    try {
      setPolicyLoading(true);
      toast.info("Loading credit policy intelligence data...");
      
      // Fetch all credit policy data in parallel
      const [summary, loanBook, comparison, hoAnalysis] = await Promise.all([
        getCreditPolicySummary(),
        getCreditPolicyLoanBook(),
        getCreditPolicyIndustryComparison(),
        getCreditPolicyHomeownerAnalysis()
      ]);
      
      setPolicyData(summary);
      setLoanBookData(loanBook);
      setIndustryComparison(comparison);
      setHomeownerAnalysis(hoAnalysis);
      
      toast.success("Credit policy data loaded successfully");
    } catch (error) {
      console.error("Failed to load credit policy data:", error);
      toast.error("Failed to load credit policy data");
    } finally {
      setPolicyLoading(false);
    }
  }, []);

  // Enrich SIC codes batch
  const handleEnrichSicCodes = async (target) => {
    try {
      setSicEnrichmentLoading(true);
      toast.info(`Enriching ${target} records with SIC codes from Companies House...`);
      
      const result = await enrichSicCodes(target, true, 50);
      
      if (result.success) {
        toast.success(`Enriched ${result.enriched} records with SIC codes`);
        // Reload the data
        await loadCreditPolicyData();
      }
    } catch (error) {
      console.error("SIC enrichment error:", error);
      toast.error("Failed to enrich SIC codes");
    } finally {
      setSicEnrichmentLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "£0";
    return new Intl.NumberFormat('en-GB', { 
      style: 'currency', 
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatCurrencyShort = (amount) => {
    if (!amount) return "£0";
    if (amount >= 1000000) return `£${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `£${(amount / 1000).toFixed(0)}K`;
    return `£${amount.toFixed(0)}`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg text-sm">
          <p className="font-semibold mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {entry.name.includes('£') || entry.name.includes('Amount') 
                ? formatCurrency(entry.value) 
                : entry.name.includes('%') 
                  ? `${entry.value?.toFixed(1)}%`
                  : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-blue-600" />
          <p className="mt-4 text-muted-foreground">Loading legal performance analysis...</p>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const loanSizeData = basicData?.loan_size_analysis 
    ? Object.entries(basicData.loan_size_analysis).map(([bucket, data]) => ({
        name: bucket,
        count: data.count,
        percentage: data.percentage,
        avgPrincipal: data.avg_principal,
        total: data.total
      }))
    : [];

  const topupData = basicData?.topup_analysis 
    ? Object.entries(basicData.topup_analysis).map(([topups, data]) => ({
        name: topups === "0" ? "No Top-ups" : topups === "1" ? "1 Top-up" : topups === "2" ? "2 Top-ups" : "3+ Top-ups",
        value: data.count,
        percentage: data.percentage
      }))
    : [];

  const fundingDayData = enrichedData?.funding_day_distribution || [];
  const industryData = enrichedData?.industry_breakdown || [];
  const hoData = enrichedData?.ho_vs_non_ho || basicData?.ho_vs_non_ho;
  const timeToDefaultData = enrichedData?.time_to_default;

  // Prepare HO vs NON-HO pie chart data
  const hoChartData = hoData ? [
    { name: "Homeowner (HO)", value: hoData.HO?.count || 0, fill: COLORS.success },
    { name: "Non-Homeowner", value: hoData["NON-HO"]?.count || 0, fill: COLORS.danger },
    { name: "Unknown", value: hoData.Unknown?.count || 0, fill: "#94a3b8" }
  ].filter(d => d.value > 0) : [];

  // Time to default chart data
  const timeToDefaultChartData = timeToDefaultData ? [
    { name: "0-90 days", value: timeToDefaultData["0-90"] || 0, fill: COLORS.success },
    { name: "91-180 days", value: timeToDefaultData["91-180"] || 0, fill: COLORS.warning },
    { name: "181-365 days", value: timeToDefaultData["181-365"] || 0, fill: COLORS.danger },
    { name: "365+ days", value: timeToDefaultData["365+"] || 0, fill: COLORS.purple }
  ].filter(d => d.value > 0) : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Gavel className="w-7 h-7 text-red-600" />
            Legal Performance Analysis
          </h1>
          <p className="text-muted-foreground">
            Deep-dive analytics on defaults, legal cases, and write-offs
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          {/* Emma #6 — handling + jurisdiction */}
          <Select value={handlingFilter} onValueChange={setHandlingFilter}>
            <SelectTrigger className="h-9 w-[190px]" data-testid="legal-perf-filter-handling">
              <SelectValue placeholder="Handling" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All handling</SelectItem>
              <SelectItem value="in_house">In-house</SelectItem>
              <SelectItem value="out_of_jurisdiction">🌍 Out of jurisdiction</SelectItem>
              <SelectItem value="external">🏛 External (instructed firm)</SelectItem>
            </SelectContent>
          </Select>
          <Select value={jurisdictionFilter} onValueChange={setJurisdictionFilter}>
            <SelectTrigger className="h-9 w-[170px]" data-testid="legal-perf-filter-jurisdiction">
              <SelectValue placeholder="Jurisdiction" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All jurisdictions</SelectItem>
              <SelectItem value="england_wales">England &amp; Wales</SelectItem>
              <SelectItem value="scotland">Scotland</SelectItem>
              <SelectItem value="northern_ireland">Northern Ireland</SelectItem>
            </SelectContent>
          </Select>
          {basicData?.filters_applied?.unfiltered_total != null && (
            <Badge variant="outline" className="h-9 px-3 text-xs bg-indigo-50 text-indigo-800 border-indigo-200" data-testid="legal-perf-filter-chip">
              {basicData.filters_applied.matched} of {basicData.filters_applied.unfiltered_total} cases match
            </Badge>
          )}
          <Button 
            variant="outline" 
            onClick={loadEnrichedData} 
            disabled={enrichedLoading}
            className="gap-2"
          >
            {enrichedLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Factory className="w-4 h-4" />
            )}
            {enrichedLoading ? "Loading..." : "Load Enriched Data"}
          </Button>
          <Button variant="outline" onClick={loadBasicData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Summary Cards */}
      {basicData && !basicData.summary && basicData.total_cases === 0 && (
        <Card className="border-amber-200 bg-amber-50/50" data-testid="legal-perf-empty">
          <CardContent className="py-4 text-sm text-amber-800 flex items-center gap-2">
            <Info className="w-4 h-4" /> {basicData.message || "No cases to analyse."}
          </CardContent>
        </Card>
      )}
      {basicData?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="pt-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-red-600 uppercase font-medium">Total Legal Cases</p>
                  <p className="text-3xl font-bold text-red-700">{basicData.summary.total_cases}</p>
                </div>
                <AlertTriangle className="w-10 h-10 text-red-300" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="pt-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-amber-600 uppercase font-medium">Principal at Risk</p>
                  <p className="text-2xl font-bold text-amber-700">{formatCurrencyShort(basicData.summary.total_principal_at_risk)}</p>
                </div>
                <PoundSterling className="w-10 h-10 text-amber-300" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="pt-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-purple-600 uppercase font-medium">Outstanding Balance</p>
                  <p className="text-2xl font-bold text-purple-700">{formatCurrencyShort(basicData.summary.total_balance_outstanding)}</p>
                </div>
                <Target className="w-10 h-10 text-purple-300" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="pt-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-blue-600 uppercase font-medium">Avg Loan Size</p>
                  <p className="text-2xl font-bold text-blue-700">{formatCurrency(basicData.summary.avg_principal)}</p>
                </div>
                <FileText className="w-10 h-10 text-blue-300" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Enriched Data Notice */}
      {!enrichedData && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Info className="w-5 h-5 text-amber-600" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  Click "Load Enriched Data" for detailed analysis
                </p>
                <p className="text-xs text-amber-600">
                  This will fetch funding dates, SIC codes, and product types from LoanDisk & Companies House
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Analysis Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="credit-policy" onClick={() => !policyData && loadCreditPolicyData()}>
            Credit Policy
          </TabsTrigger>
          <TabsTrigger value="funding-patterns">Funding Patterns</TabsTrigger>
          <TabsTrigger value="industry">Industry Analysis</TabsTrigger>
          <TabsTrigger value="loan-size">Loan Size</TabsTrigger>
          <TabsTrigger value="ho-analysis">HO vs Non-HO</TabsTrigger>
          <TabsTrigger value="topups">Top-up Analysis</TabsTrigger>
          <TabsTrigger value="timeline">Default Timeline</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Loan Size Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PoundSterling className="w-5 h-5 text-blue-600" />
                  Loan Size Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={loanSizeData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="count" name="Cases" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 space-y-2">
                  {loanSizeData.map((item, idx) => (
                    <div key={item.name} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{item.name}</span>
                      <div className="flex items-center gap-4">
                        <span className="font-medium">{item.count} cases</span>
                        <Badge variant="outline">{item.percentage}%</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Top-up Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Repeat className="w-5 h-5 text-purple-600" />
                  Top-up History Before Default
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={topupData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percentage }) => `${name}: ${percentage}%`}
                        labelLine={false}
                      >
                        {topupData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  {topupData.map((item, idx) => (
                    <div key={item.name} className="flex items-center gap-2 text-sm">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: CHART_COLORS[idx] }} />
                      <span className="text-muted-foreground">{item.name}:</span>
                      <span className="font-medium">{item.value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Credit Policy Intelligence Tab */}
        <TabsContent value="credit-policy">
          {policyLoading ? (
            <div className="flex items-center justify-center h-[400px]">
              <div className="text-center">
                <Loader2 className="w-12 h-12 animate-spin mx-auto text-blue-600" />
                <p className="mt-4 text-muted-foreground">Loading credit policy intelligence...</p>
              </div>
            </div>
          ) : !policyData ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <Factory className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Credit Policy Intelligence</h3>
                <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                  Analyze legal cases and full loan book to identify high-risk and low-risk industries, 
                  restructure patterns, and optimize credit policy.
                </p>
                <Button onClick={loadCreditPolicyData} className="gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Load Credit Policy Data
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                  <CardContent className="pt-5">
                    <p className="text-xs text-red-600 uppercase font-medium">Legal Cases</p>
                    <p className="text-2xl font-bold text-red-700">{policyData.summary?.total_legal_cases}</p>
                    <p className="text-xs text-red-600">{formatCurrencyShort(policyData.summary?.total_legal_principal)} at risk</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="pt-5">
                    <p className="text-xs text-blue-600 uppercase font-medium">Total Loan Book</p>
                    <p className="text-2xl font-bold text-blue-700">{policyData.summary?.total_loans}</p>
                    <p className="text-xs text-blue-600">{formatCurrencyShort(policyData.summary?.total_book_principal)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                  <CardContent className="pt-5">
                    <p className="text-xs text-amber-600 uppercase font-medium">Default Rate</p>
                    <p className="text-2xl font-bold text-amber-700">{policyData.summary?.overall_default_rate_pct}%</p>
                    <p className="text-xs text-amber-600">of loans go legal</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="pt-5">
                    <p className="text-xs text-purple-600 uppercase font-medium">Restructures</p>
                    <p className="text-2xl font-bold text-purple-700">{loanBookData?.restructure_analysis?.total_restructures || 0}</p>
                    <p className="text-xs text-purple-600">{loanBookData?.restructure_analysis?.restructure_rate_pct || 0}% rate</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <CardContent className="pt-5">
                    <p className="text-xs text-green-600 uppercase font-medium">Legal as % Book</p>
                    <p className="text-2xl font-bold text-green-700">{policyData.summary?.legal_as_pct_of_book}%</p>
                    <p className="text-xs text-green-600">by principal</p>
                  </CardContent>
                </Card>
              </div>

              {/* SIC Code Enrichment Actions */}
              <Card className="border-amber-200 bg-amber-50/30">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Info className="w-5 h-5 text-amber-600" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">Industry Analysis Enhancement</p>
                        <p className="text-xs text-amber-600">
                          Enrich records with SIC codes from Companies House for better industry breakdown
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEnrichSicCodes('legal')}
                        disabled={sicEnrichmentLoading}
                        className="gap-2"
                      >
                        {sicEnrichmentLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Building2 className="w-3 h-3" />}
                        Enrich Legal Cases
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEnrichSicCodes('loanbook')}
                        disabled={sicEnrichmentLoading}
                        className="gap-2"
                      >
                        {sicEnrichmentLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Building2 className="w-3 h-3" />}
                        Enrich Loan Book
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* High Risk Industries */}
                <Card className="border-red-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                      <AlertTriangle className="w-5 h-5" />
                      High Risk Industries
                    </CardTitle>
                    <p className="text-xs text-muted-foreground">Industries over-represented in legal cases</p>
                  </CardHeader>
                  <CardContent>
                    {industryComparison?.high_risk_industries?.length > 0 ? (
                      <div className="space-y-3">
                        {industryComparison.high_risk_industries.slice(0, 5).map((ind, idx) => (
                          <div key={idx} className="p-3 bg-red-50 rounded-lg border border-red-100">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm">{ind.industry}</span>
                              <Badge className="bg-red-100 text-red-700">{ind.default_rate}% default</Badge>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                              <div>Legal: {ind.legal_count} ({ind.legal_pct}%)</div>
                              <div>Total: {ind.total_loans} ({ind.total_pct}%)</div>
                              <div className="text-red-600 font-medium">+{ind.over_representation}% over</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Enrich SIC codes to see industry breakdown
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Low Risk Industries */}
                <Card className="border-green-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2 text-green-700">
                      <TrendingUp className="w-5 h-5" />
                      Low Risk Industries
                    </CardTitle>
                    <p className="text-xs text-muted-foreground">Industries under-represented in legal cases</p>
                  </CardHeader>
                  <CardContent>
                    {industryComparison?.low_risk_industries?.length > 0 ? (
                      <div className="space-y-3">
                        {industryComparison.low_risk_industries.slice(0, 5).map((ind, idx) => (
                          <div key={idx} className="p-3 bg-green-50 rounded-lg border border-green-100">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm">{ind.industry}</span>
                              <Badge className="bg-green-100 text-green-700">{ind.default_rate}% default</Badge>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                              <div>Legal: {ind.legal_count} ({ind.legal_pct}%)</div>
                              <div>Total: {ind.total_loans} ({ind.total_pct}%)</div>
                              <div className="text-green-600 font-medium">{ind.over_representation}% under</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Enrich SIC codes to see industry breakdown
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Loan Size Analysis - Legal vs Total Book */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <PoundSterling className="w-5 h-5 text-blue-600" />
                    Loan Size Risk Analysis
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Compare default rates across loan size bands
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-5 gap-3">
                    {policyData?.legal_analysis?.by_loan_size?.map((bucket, idx) => {
                      const totalBucket = policyData?.loan_book_analysis?.by_loan_size?.find(b => b.bucket === bucket.bucket);
                      const defaultRate = totalBucket?.count > 0 ? ((bucket.count / totalBucket.count) * 100).toFixed(1) : 0;
                      const isHighRisk = parseFloat(defaultRate) > 10;
                      
                      return (
                        <Card key={idx} className={`${isHighRisk ? 'border-red-200 bg-red-50/30' : 'border-green-200 bg-green-50/30'}`}>
                          <CardContent className="pt-4 text-center">
                            <p className="text-xs font-medium text-muted-foreground mb-1">{bucket.bucket}</p>
                            <p className={`text-2xl font-bold ${isHighRisk ? 'text-red-700' : 'text-green-700'}`}>
                              {defaultRate}%
                            </p>
                            <p className="text-[10px] text-muted-foreground mt-1">
                              {bucket.count} / {totalBucket?.count || 0} cases
                            </p>
                            <p className="text-xs font-medium mt-2">
                              {formatCurrencyShort(bucket.total_principal)} at risk
                            </p>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-800 flex items-center gap-2 mb-2">
                      <Info className="w-4 h-4" />
                      Policy Insight
                    </h4>
                    <p className="text-sm text-blue-700">
                      {(() => {
                        const highRiskBuckets = policyData?.legal_analysis?.by_loan_size?.filter(b => {
                          const total = policyData?.loan_book_analysis?.by_loan_size?.find(t => t.bucket === b.bucket);
                          return total?.count > 0 && (b.count / total.count) > 0.12;
                        }) || [];
                        if (highRiskBuckets.length > 0) {
                          return `Consider tightening criteria for ${highRiskBuckets.map(b => b.bucket).join(', ')} loans where default rates exceed 12%.`;
                        }
                        return 'Default rates are relatively consistent across loan size bands. Focus on industry-specific risk factors.';
                      })()}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Homeowner Analysis */}
              {homeownerAnalysis && (
                <Card className="border-blue-200">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Home className="w-5 h-5 text-blue-600" />
                      Homeowner vs Non-Homeowner Analysis
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Compare default rates between HO and NONHO secured loans
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* HO Breakdown Cards */}
                      <div className="grid grid-cols-3 gap-3">
                        {homeownerAnalysis.ho_comparison?.comparison?.map((item, idx) => {
                          const isHighRisk = item.default_rate > 10;
                          const isMediumRisk = item.default_rate > 7 && item.default_rate <= 10;
                          return (
                            <Card key={idx} className={`${
                              isHighRisk ? 'border-red-200 bg-red-50/50' : 
                              isMediumRisk ? 'border-amber-200 bg-amber-50/50' : 
                              'border-green-200 bg-green-50/50'
                            }`}>
                              <CardContent className="pt-4 text-center">
                                <Badge className={`mb-2 ${
                                  item.homeowner_status === 'HO' ? 'bg-blue-100 text-blue-700' :
                                  item.homeowner_status === 'NONHO' ? 'bg-orange-100 text-orange-700' :
                                  'bg-slate-100'
                                }`}>
                                  {item.homeowner_status === 'HO' ? 'Homeowner' : 
                                   item.homeowner_status === 'NONHO' ? 'Non-Homeowner' : 'Unknown'}
                                </Badge>
                                <p className={`text-3xl font-bold ${
                                  isHighRisk ? 'text-red-700' : 
                                  isMediumRisk ? 'text-amber-700' : 'text-green-700'
                                }`}>
                                  {item.default_rate}%
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">Default Rate</p>
                                <div className="mt-3 pt-3 border-t text-xs">
                                  <p><span className="text-red-600">{item.legal_count}</span> legal / {item.total_count} total</p>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>

                      {/* Insight Panel */}
                      <div className="space-y-4">
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <h4 className="font-semibold text-blue-800 flex items-center gap-2 mb-2">
                            <Info className="w-4 h-4" />
                            Credit Policy Insight
                          </h4>
                          <p className="text-sm text-blue-700">
                            {homeownerAnalysis.ho_comparison?.insight}
                          </p>
                        </div>

                        {/* Book Breakdown */}
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <p className="text-xs font-semibold text-muted-foreground mb-3">LOAN BOOK COMPOSITION</p>
                          <div className="space-y-2">
                            {homeownerAnalysis.loan_book_ho_breakdown?.by_homeowner_status?.filter(h => h.homeowner_status !== 'Unknown').map((ho, idx) => (
                              <div key={idx} className="flex items-center justify-between">
                                <span className="text-sm flex items-center gap-2">
                                  {ho.homeowner_status === 'HO' ? (
                                    <Home className="w-4 h-4 text-blue-500" />
                                  ) : (
                                    <Building2 className="w-4 h-4 text-orange-500" />
                                  )}
                                  {ho.homeowner_status === 'HO' ? 'Homeowner' : 'Non-Homeowner'}
                                </span>
                                <div className="flex items-center gap-3">
                                  <Progress value={ho.count_pct} className="w-24 h-2" />
                                  <span className="text-sm font-medium w-16 text-right">{ho.count} ({ho.count_pct}%)</span>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="mt-3 pt-3 border-t text-xs text-muted-foreground">
                            Principal: HO {formatCurrencyShort(homeownerAnalysis.loan_book_ho_breakdown?.by_homeowner_status?.find(h => h.homeowner_status === 'HO')?.total_principal || 0)} | 
                            NONHO {formatCurrencyShort(homeownerAnalysis.loan_book_ho_breakdown?.by_homeowner_status?.find(h => h.homeowner_status === 'NONHO')?.total_principal || 0)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Restructure Analysis */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Repeat className="w-5 h-5 text-purple-600" />
                    Restructure Analysis
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Track loan modifications and renegotiations
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Restructure Overview */}
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-3">
                        <div className="p-3 bg-purple-50 rounded-lg text-center">
                          <p className="text-2xl font-bold text-purple-700">{loanBookData?.restructure_analysis?.total_restructures || 0}</p>
                          <p className="text-xs text-purple-600">Total Restructures</p>
                        </div>
                        <div className="p-3 bg-purple-50 rounded-lg text-center">
                          <p className="text-2xl font-bold text-purple-700">{loanBookData?.restructure_analysis?.restructure_rate_pct || 0}%</p>
                          <p className="text-xs text-purple-600">Restructure Rate</p>
                        </div>
                        <div className="p-3 bg-amber-50 rounded-lg text-center">
                          <p className="text-2xl font-bold text-amber-700">{loanBookData?.restructure_analysis?.zero_interest_count || 0}</p>
                          <p className="text-xs text-amber-600">0% Interest Deals</p>
                        </div>
                      </div>
                      
                      {/* Restructure count breakdown */}
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-xs font-semibold text-muted-foreground mb-2">BY RESTRUCTURE COUNT</p>
                        <div className="space-y-2">
                          {Object.entries(loanBookData?.restructure_analysis?.by_restructure_count || {}).map(([count, value]) => (
                            <div key={count} className="flex items-center justify-between">
                              <span className="text-sm">{count === '4+' ? '4+ times' : `${count} times`}</span>
                              <div className="flex items-center gap-2">
                                <Progress value={(value / (loanBookData?.restructure_analysis?.total_restructures || 1)) * 100} className="w-24 h-2" />
                                <span className="text-sm font-medium w-10 text-right">{value}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Restructure by Industry */}
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground mb-3">TOP RESTRUCTURE INDUSTRIES</p>
                      <div className="space-y-2 max-h-[250px] overflow-y-auto">
                        {loanBookData?.restructure_analysis?.by_industry?.slice(0, 10).map((ind, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                            <span className="text-xs truncate flex-1 mr-2">{ind.industry}</span>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-muted-foreground">{ind.restructured_count}/{ind.total}</span>
                              <Badge variant={ind.restructure_rate > 15 ? "destructive" : "secondary"} className="text-[10px]">
                                {ind.restructure_rate}%
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Refresh Button */}
              <div className="flex justify-end">
                <Button variant="outline" onClick={loadCreditPolicyData} disabled={policyLoading}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${policyLoading ? 'animate-spin' : ''}`} />
                  Refresh Credit Policy Data
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Funding Patterns Tab */}
        <TabsContent value="funding-patterns">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Funding Day Distribution
                <Badge variant="outline" className="ml-2">Month-End Rush Analysis</Badge>
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Identifies if credit team is rushing deals through to hit month-end targets
              </p>
            </CardHeader>
            <CardContent>
              {enrichedData?.funding_day_distribution?.length > 0 ? (
                <>
                  <div className="h-[400px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={fundingDayData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                        <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                        <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => formatCurrencyShort(v)} tick={{ fontSize: 11 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar yAxisId="left" dataKey="count" name="Legal Cases" fill={COLORS.danger} radius={[4, 4, 0, 0]} />
                        <Line yAxisId="right" type="monotone" dataKey="total_amount" name="£ Amount" stroke={COLORS.primary} strokeWidth={2} dot={{ r: 3 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                  
                  {/* Month-end analysis insight */}
                  <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <h4 className="font-semibold text-amber-800 flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4" />
                      Month-End Pattern Analysis
                    </h4>
                    {(() => {
                      const lastWeekDays = fundingDayData.filter(d => d.day >= 25);
                      const firstWeekDays = fundingDayData.filter(d => d.day <= 7);
                      const lastWeekCount = lastWeekDays.reduce((sum, d) => sum + d.count, 0);
                      const firstWeekCount = firstWeekDays.reduce((sum, d) => sum + d.count, 0);
                      const totalCount = fundingDayData.reduce((sum, d) => sum + d.count, 0);
                      const lastWeekPct = totalCount > 0 ? ((lastWeekCount / totalCount) * 100).toFixed(1) : 0;
                      
                      return (
                        <div className="text-sm text-amber-700">
                          <p>
                            <strong>{lastWeekPct}%</strong> of legal cases were funded in the last week of the month (days 25-31).
                          </p>
                          {parseFloat(lastWeekPct) > 30 && (
                            <p className="mt-2 text-red-700 font-medium">
                              ⚠️ High concentration of defaults from month-end funding - possible rush deals
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </>
              ) : (
                <div className="h-[400px] flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <Calendar className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>Load enriched data to see funding day patterns</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Industry Analysis Tab */}
        <TabsContent value="industry">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Factory className="w-5 h-5 text-indigo-600" />
                Industry Breakdown (SIC Codes)
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Which industries are contributing most to legal/write-off cases
              </p>
            </CardHeader>
            <CardContent>
              {industryData.length > 0 ? (
                <>
                  <div className="h-[400px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={industryData.map(([code, data]) => ({ 
                        code, 
                        description: data.description,
                        count: data.count,
                        total: data.total_principal
                      }))} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis type="number" tick={{ fontSize: 12 }} />
                        <YAxis type="category" dataKey="description" tick={{ fontSize: 10 }} width={150} />
                        <Tooltip 
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              return (
                                <div className="bg-white p-3 border rounded-lg shadow-lg text-sm">
                                  <p className="font-semibold">{data.description}</p>
                                  <p className="text-muted-foreground text-xs">SIC: {data.code}</p>
                                  <p className="mt-2">Cases: <strong>{data.count}</strong></p>
                                  <p>Total Principal: <strong>{formatCurrency(data.total)}</strong></p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Bar dataKey="count" name="Cases" fill={COLORS.indigo} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  
                  {/* Industry risk table */}
                  <div className="mt-6">
                    <h4 className="font-semibold mb-3">Top Risk Industries</h4>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-4 py-2 text-left">Industry</th>
                            <th className="px-4 py-2 text-left">SIC Code</th>
                            <th className="px-4 py-2 text-right">Cases</th>
                            <th className="px-4 py-2 text-right">Total Principal</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {industryData.slice(0, 10).map(([code, data], idx) => (
                            <tr key={code} className="hover:bg-slate-50">
                              <td className="px-4 py-2">{data.description}</td>
                              <td className="px-4 py-2 font-mono text-xs">{code}</td>
                              <td className="px-4 py-2 text-right font-semibold">{data.count}</td>
                              <td className="px-4 py-2 text-right text-red-600 font-medium">{formatCurrency(data.total_principal)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              ) : (
                <div className="h-[400px] flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <Factory className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>Load enriched data to see industry breakdown</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Loan Size Tab */}
        <TabsContent value="loan-size">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <PoundSterling className="w-5 h-5 text-green-600" />
                Loan Size Analysis on Bad Debt
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Correlation between loan amount and likelihood of going legal
              </p>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={loanSizeData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="count" name="Cases" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                    <Line yAxisId="right" type="monotone" dataKey="percentage" name="% of Total" stroke={COLORS.danger} strokeWidth={3} dot={{ r: 5 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              
              {/* Insights */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                {loanSizeData.map((bucket) => (
                  <div key={bucket.name} className="p-4 bg-slate-50 rounded-lg border">
                    <p className="font-semibold text-lg">{bucket.name}</p>
                    <div className="mt-2 space-y-1 text-sm">
                      <p className="text-muted-foreground">
                        Cases: <span className="font-medium text-foreground">{bucket.count}</span>
                      </p>
                      <p className="text-muted-foreground">
                        % of defaults: <span className="font-medium text-foreground">{bucket.percentage}%</span>
                      </p>
                      <p className="text-muted-foreground">
                        Avg size: <span className="font-medium text-foreground">{formatCurrency(bucket.avgPrincipal)}</span>
                      </p>
                    </div>
                    <Progress value={bucket.percentage} className="mt-3 h-2" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* HO vs Non-HO Tab */}
        <TabsContent value="ho-analysis">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Home className="w-5 h-5 text-teal-600" />
                Homeowner vs Non-Homeowner Analysis
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Product type breakdown for legal cases (HO vs NON-HO in LoanDisk)
              </p>
            </CardHeader>
            <CardContent>
              {hoChartData.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={hoChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={70}
                          outerRadius={120}
                          paddingAngle={3}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {hoChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  
                  <div className="space-y-4">
                    {hoData && (
                      <>
                        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                          <div className="flex items-center gap-2 mb-2">
                            <Home className="w-5 h-5 text-green-600" />
                            <h4 className="font-semibold text-green-800">Homeowner (HO)</h4>
                          </div>
                          <p className="text-2xl font-bold text-green-700">{hoData.HO?.count || 0} cases</p>
                          <p className="text-sm text-green-600">
                            Total Principal: {formatCurrency(hoData.HO?.total || 0)}
                          </p>
                        </div>
                        
                        <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                          <div className="flex items-center gap-2 mb-2">
                            <Building2 className="w-5 h-5 text-red-600" />
                            <h4 className="font-semibold text-red-800">Non-Homeowner (NON-HO)</h4>
                          </div>
                          <p className="text-2xl font-bold text-red-700">{hoData["NON-HO"]?.count || 0} cases</p>
                          <p className="text-sm text-red-600">
                            Total Principal: {formatCurrency(hoData["NON-HO"]?.total || 0)}
                          </p>
                        </div>
                        
                        {hoData.Unknown?.count > 0 && (
                          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                            <h4 className="font-semibold text-slate-700">Unknown Product Type</h4>
                            <p className="text-xl font-bold text-slate-600">{hoData.Unknown.count} cases</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="h-[350px] flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <Home className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>Load enriched data to see HO vs Non-HO breakdown</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Top-ups Tab */}
        <TabsContent value="topups">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Repeat className="w-5 h-5 text-purple-600" />
                Top-up Analysis - Defaults After Multiple Loans
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                After how many top-ups are customers going bust
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topupData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={100} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" name="Cases" radius={[0, 4, 4, 0]}>
                        {topupData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="space-y-4">
                  {topupData.map((item, idx) => (
                    <div key={item.name} className="p-4 rounded-lg border" style={{ borderLeftWidth: 4, borderLeftColor: CHART_COLORS[idx] }}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold">{item.name}</h4>
                          <p className="text-sm text-muted-foreground">
                            {item.value} legal cases ({item.percentage}%)
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-3xl font-bold" style={{ color: CHART_COLORS[idx] }}>
                            {item.percentage}%
                          </p>
                        </div>
                      </div>
                      <Progress value={item.percentage} className="mt-3 h-2" />
                    </div>
                  ))}
                  
                  {/* Insight */}
                  {topupData.length > 0 && (
                    <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <h4 className="font-semibold text-purple-800 flex items-center gap-2 mb-2">
                        <Info className="w-4 h-4" />
                        Key Insight
                      </h4>
                      <p className="text-sm text-purple-700">
                        {topupData[0]?.percentage > 50 
                          ? "Most defaults occur on first-time loans, suggesting issues in initial credit assessment."
                          : "Defaults are spread across loan cycles, indicating ongoing monitoring is important."}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Default Timeline Tab */}
        <TabsContent value="timeline">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-600" />
                Time to Default Analysis
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Average payment patterns - when are customers getting into default/legal territory
              </p>
            </CardHeader>
            <CardContent>
              {timeToDefaultChartData.length > 0 ? (
                <>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={timeToDefaultChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={70}
                          outerRadius={120}
                          paddingAngle={3}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {timeToDefaultChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  
                  <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {timeToDefaultChartData.map((item) => (
                      <div key={item.name} className="p-4 rounded-lg text-center" style={{ backgroundColor: `${item.fill}15`, borderColor: item.fill, borderWidth: 1 }}>
                        <p className="text-sm text-muted-foreground">{item.name}</p>
                        <p className="text-2xl font-bold mt-1" style={{ color: item.fill }}>{item.value}</p>
                        <p className="text-xs text-muted-foreground">cases</p>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="h-[350px] flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <Clock className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>Load enriched data to see time-to-default analysis</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Case Details Table */}
      {basicData?.case_sample?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Sample Legal Cases</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="px-4 py-3 text-left">SF Number</th>
                    <th className="px-4 py-3 text-left">Company</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Broker</th>
                    <th className="px-4 py-3 text-right">Principal</th>
                    <th className="px-4 py-3 text-right">Balance</th>
                    <th className="px-4 py-3 text-center">Top-ups</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {basicData.case_sample.map((c) => (
                    <tr key={c.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono text-xs">{c.sf_number}</td>
                      <td className="px-4 py-3 max-w-[200px] truncate">{c.company_name}</td>
                      <td className="px-4 py-3">
                        <Badge variant={c.loan_status?.toLowerCase() === 'legal' ? 'destructive' : 'secondary'}>
                          {c.loan_status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{c.broker || '-'}</td>
                      <td className="px-4 py-3 text-right font-medium">{formatCurrency(c.principal)}</td>
                      <td className="px-4 py-3 text-right text-red-600 font-medium">{formatCurrency(c.balance)}</td>
                      <td className="px-4 py-3 text-center">
                        {c.topup_count > 0 ? (
                          <Badge variant="outline" className="bg-purple-50">{c.topup_count}</Badge>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
